import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Plus, Trash2, TriangleAlert, Loader2, Save, Undo2, Waypoints } from 'lucide-react';
import { useAppContext } from '../../../context/AppContext';
import { secondaryService } from '../../../services/secondaryService';
import SearchSelect from '../../../components/SearchSelect';
import LaneNetworkMap from '../../../components/LaneNetworkMap';

// =============================================================================
// Distribution › Route to Market › Secondary Lanes & Rates
//
// Sits in ROUTE TO MARKET, not Budget Setup: who carries a lane at what rate is
// the distribution manager's own decision. Only the POSTING catalogue is a
// chart-of-accounts decision with a different owner, and that stays in Budget
// Setup — the same split as the inter-depot rating page vs Inter-Depot Posting.
//
// ONE THING ONLY: the rate card. Zones, customer → zone mapping and the store
// delivery calendar moved to Delivery Zones & Customers next door. They are
// master data about WHERE you deliver and WHO you deliver to, edited together
// and rarely; this page is about WHO MOVES IT and AT WHAT PRICE, edited every
// time a contract is renegotiated. They were tabs on one page and the split was
// in the wrong place.
//
//   JHB-DC → Gauteng Rural, and WHO MOVES WHAT SHARE:
//
//     Own fleet        60%   Rigid 8t
//       per km    R14.20
//       per drop  R180
//     Swift Logistics  40%   Rigid 8t
//       per drop  R620
//
//   ALLOCATION % IS ON THE CARRIER. A quote is routinely several components —
//   "R450 a drop PLUS R50 a pallet" — and those are one carrier's price, not
//   competitors for a share. Carriers sum to 100; components do not sum at all.
//
// SAVE BUTTON, not autosave. Adds and deletes still commit immediately because
// they need a server id to edit against — but no typed value is written until
// Save.
// =============================================================================

const STREAMS = ['AMBIENT', 'CHILLED', 'FROZEN'];
const RATE_BASES = [
  { code: 'PER_DROP', label: 'Per drop', driver: 'drops', hint: 'rate × this carrier’s drops' },
  { code: 'PER_UNIT', label: 'Per load unit', driver: 'volume', hint: 'rate × this carrier’s pallets / cages' },
  { code: 'PER_RUN', label: 'Per run', driver: 'runs', hint: 'rate × this carrier’s runs (milk run / fixed route)' },
  { code: 'PER_KM', label: 'Per km', driver: 'km', hint: 'rate × this carrier’s runs × round-trip km' },
];

const field = 'text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-30';
const numField = `${field} text-right`;
const btn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';
const th = 'px-2 py-2 font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase text-[10px] whitespace-nowrap';

const Pill = ({ tone, children }) => <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${tone}`}>{children}</span>;
const TONE = {
  owned: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
};

const sum12 = (a) => (a || []).reduce((x, v) => x + (Number(v) || 0), 0);
const fmtR = (n, dp = 0) => `R ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const fmtN = (n, dp = 0) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
const streamLabel = (s) => (s ? `${s[0]}${s.slice(1).toLowerCase()}` : '');

// A site code on its own is unreadable — "JHB01" means nothing to anyone who
// did not build the site master. Every "served from" carries the description.
const siteLabel = (code, name) => (name ? `${code} · ${name}` : (code || ''));

const SecondaryLanes = () => {
  const { selectedVersion, selectedSite } = useAppContext();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [setup, setSetup] = useState({
    zones: [], lanes: [], carriers: [], rates: [],
    picks: { contractors: [], vehicleTypes: [], sites: [], priceIndices: [] },
    config: {
      store_replenishment_enabled: false, secondary_run_rounding: 'CEIL',
      fuel_share_defaults: { PER_KM: 40, PER_RUN: 35, PER_DROP: 20, PER_UNIT: 10 },
    },
  });

  // The rate card opens first; the map is a second way in. Same order as
  // inter-depot, and for the same reason — this is a rating page.
  const [view, setView] = useState('rates');   // 'rates' | 'network'

  const [selLane, setSelLane] = useState(null);
  const [laneDirty, setLaneDirty] = useState({ lanes: {}, carriers: {}, rates: {} });
  const [pendingLane, setPendingLane] = useState(null);   // lane the user tried to switch to
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await secondaryService.getSetup();
      const d = res.data || {};
      setSetup((prev) => ({ ...prev, ...d }));
      setSelLane((cur) => (cur && (d.lanes || []).some((l) => l.lane_id === cur) ? cur : (d.lanes || [])[0]?.lane_id || null));
      setLaneDirty({ lanes: {}, carriers: {}, rates: {} });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the secondary lane setup.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion, selectedSite]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const run = async (fn, msg) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
      if (msg) setToast(msg);
      return true;
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'That did not save.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const lane = setup.lanes.find((l) => l.lane_id === selLane) || setup.lanes[0] || null;
  const laneCarriers = useMemo(
    () => setup.carriers.filter((c) => c.lane_id === lane?.lane_id),
    [setup.carriers, lane]
  );

  const laneVal = (l) => ({ ...l, ...(laneDirty.lanes[l.lane_id] || {}) });
  const carrierVal = (c) => ({ ...c, ...(laneDirty.carriers[c.carrier_id] || {}) });
  const rateVal = (r) => ({ ...r, ...(laneDirty.rates[r.rate_line_id] || {}) });

  const laneHasChanges = Object.keys(laneDirty.lanes).length
    + Object.keys(laneDirty.carriers).length
    + Object.keys(laneDirty.rates).length > 0;

  const patchLane = (id, p) => setLaneDirty((d) => ({ ...d, lanes: { ...d.lanes, [id]: { ...(d.lanes[id] || {}), ...p } } }));
  const patchCarrier = (id, p) => setLaneDirty((d) => ({ ...d, carriers: { ...d.carriers, [id]: { ...(d.carriers[id] || {}), ...p } } }));
  const patchRate = (id, p) => setLaneDirty((d) => ({ ...d, rates: { ...d.rates, [id]: { ...(d.rates[id] || {}), ...p } } }));

  // ---- Price-index linking --------------------------------------------------
  // Linking stamps the base from the index and the share from the policy, both
  // overridable on the line. The base is COPIED rather than read live because
  // two carriers quoted six months apart must not escalate off one base — the
  // later one would be charged for a rise already inside its price.
  const indices = useMemo(() => setup.picks.priceIndices || [], [setup.picks.priceIndices]);
  const indexByCode = useMemo(() => new Map(indices.map((i) => [i.index_code, i])), [indices]);
  const shareDefaults = setup.config.fuel_share_defaults || {};

  const linkRate = (r0, code) => {
    if (!code) { patchRate(r0.rate_line_id, { index_code: null, index_base_value: null, index_share_pct: 0 }); return; }
    const idx = indexByCode.get(code);
    const basis = (rateVal(r0).rate_basis) || r0.rate_basis;
    patchRate(r0.rate_line_id, {
      index_code: code,
      index_base_value: Number(idx?.default_base_value) || 0,
      index_share_pct: Number(shareDefaults[basis] ?? 0),
    });
  };

  // 1 + share x (now/base - 1). Shown against the YEAR AVERAGE, because the
  // page edits one annual rate — the cost view does it month by month.
  const factorOf = (r) => {
    const idx = indexByCode.get(r.index_code);
    const base = Number(r.index_base_value) || 0;
    const now = Number(idx?.avg_value) || 0;
    const share = Number(r.index_share_pct) || 0;
    if (!r.index_code || base <= 0 || now <= 0 || share === 0) return 1;
    return 1 + (share / 100) * ((now / base) - 1);
  };

  // The carrier's expected annual increase. Unlike the index it lives on the
  // CARRIER, not the rate component — a lane's carriers each have their own
  // contract — so it is looked up through the carrier the component sits under.
  const contractorByCode = useMemo(
    () => new Map((setup.picks.contractors || []).map((c) => [c.contractor_code, c])),
    [setup.picks.contractors]
  );
  const increaseOf = (carrier) => {
    if (!carrier || carrier.carrier_type === 'OWNED') return null;
    const c = contractorByCode.get(carrier.contractor_code);
    if (!c || !Number(c.increase_pct) || !c.increase_period) return null;
    return { pct: Number(c.increase_pct), period: Number(c.increase_period) };
  };
  // As the row will feel it ON AVERAGE over the year: a step in July is only
  // two-thirds of a 6% rise across twelve periods, and showing the full 6% next
  // to the rate would overstate what it costs. The cost view applies the step
  // properly, month by month.
  const increaseFactorOf = (r, carrier) => {
    if (r.apply_contractor_increase === false) return 1;
    const inc = increaseOf(carrier);
    if (!inc || inc.period > 12) return 1;
    return 1 + (inc.pct / 100) * ((12 - inc.period + 1) / 12);
  };

  // Allocation total per lane — drives the red flag in the list, so it is
  // computed for EVERY lane, not just the selected one. A lane that does not
  // add to 100 is under- or over-costed by exactly the gap, and finding that
  // out at submission is too late.
  const shareByLane = useMemo(() => {
    const m = new Map();
    setup.lanes.forEach((l) => {
      const total = setup.carriers
        .filter((c) => c.lane_id === l.lane_id)
        .reduce((a, c) => a + Number(carrierVal(c).allocation_pct || 0), 0);
      m.set(l.lane_id, { total, count: setup.carriers.filter((c) => c.lane_id === l.lane_id).length });
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup.lanes, setup.carriers, laneDirty.carriers]);

  const badLanes = useMemo(
    () => [...shareByLane.entries()].filter(([, v]) => v.count > 0 && Math.round(v.total) !== 100).length,
    [shareByLane]
  );

  const laneShare = lane ? (shareByLane.get(lane.lane_id)?.total ?? 0) : 0;
  const laneCost = laneCarriers.reduce((a, c) => a
    + setup.rates.filter((r) => r.carrier_id === c.carrier_id).reduce((b, r) => b + sum12(r.cost), 0), 0);
  const runsSplit = laneCarriers.reduce((a, c) => a + sum12(c.drivers?.runs), 0);
  const runsWhole = lane
    ? sum12((lane.drivers?.drops || []).map((d) => Math.ceil(Number(d) / (Number(laneVal(lane).drops_per_run) || 1))))
    : 0;

  const saveLaneChanges = async () => {
    const ok = await run(async () => {
      for (const [id, p] of Object.entries(laneDirty.lanes)) {
        // eslint-disable-next-line no-await-in-loop
        await secondaryService.saveLane(id, p);
      }
      for (const [id, p] of Object.entries(laneDirty.carriers)) {
        // eslint-disable-next-line no-await-in-loop
        await secondaryService.saveCarrier(id, p);
      }
      for (const [id, p] of Object.entries(laneDirty.rates)) {
        const body = { ...p };
        if (body.rate != null) { body.rates = Array(12).fill(Number(body.rate) || 0); delete body.rate; }
        // The link travels as a unit or not at all — the API only rewrites the
        // three index columns when index_code is present in the body, so a
        // partial patch can never leave a base behind without its index.
        if ('apply_contractor_increase' in body) body.apply_contractor_increase = !!body.apply_contractor_increase;
        if ('index_code' in body || 'index_base_value' in body || 'index_share_pct' in body) {
          const cur = { ...(setup.rates.find((r) => r.rate_line_id === id) || {}), ...p };
          body.index_code = cur.index_code || null;
          body.index_base_value = cur.index_code ? Number(cur.index_base_value) || 0 : null;
          body.index_share_pct = cur.index_code ? Number(cur.index_share_pct) || 0 : 0;
        }
        // eslint-disable-next-line no-await-in-loop
        await secondaryService.saveRate(id, body);
      }
    }, 'Lane saved.');
    return ok;
  };

  // Switching lane with unsaved edits asks rather than guessing. Three answers,
  // because two is not enough: discarding and cancelling are different
  // intentions and a confirm() box cannot tell them apart.
  const requestLane = (id) => {
    if (id === lane?.lane_id) return;
    if (laneHasChanges) { setPendingLane(id); return; }
    setSelLane(id);
  };
  // ---- Network view ----------------------------------------------------------
  // NOT the same graph as inter-depot. A secondary lane runs site → delivery
  // ZONE, so the far end is a geography, not a depot: the map is a star out of
  // each origin rather than a mesh. Zones sit on tier 1 and never dispatch.
  const netNodes = useMemo(() => {
    const out = [];
    const origins = new Map();
    setup.lanes.forEach((l) => {
      if (!origins.has(l.origin_site_code)) {
        const s = setup.picks.sites?.find((x) => x.site_code === l.origin_site_code);
        origins.set(l.origin_site_code, {
          id: `S:${l.origin_site_code}`,
          label: s?.site_name || l.origin_site_code,
          sublabel: `${l.origin_site_code} · depot`,
          tier: 0, colour: '#0284c7', lanes: 0, cost: 0,
        });
      }
      const o = origins.get(l.origin_site_code);
      o.lanes += 1;
      o.cost += setup.carriers.filter((c) => c.lane_id === l.lane_id)
        .reduce((a, c) => a + setup.rates.filter((r) => r.carrier_id === c.carrier_id)
          .reduce((b, r) => b + sum12(r.cost), 0), 0);
    });
    origins.forEach((o) => out.push({ ...o, metric: `${o.lanes} zone${o.lanes === 1 ? '' : 's'} · ${fmtR(o.cost)}` }));

    setup.lanes.forEach((l) => {
      const id = `Z:${l.zone_code}:${l.stream || ''}`;
      if (out.some((n) => n.id === id)) return;
      const z = setup.zones.find((x) => x.zone_code === l.zone_code);
      out.push({
        id,
        label: l.zone_name || l.zone_code,
        sublabel: `${l.zone_code}${l.stream ? ` · ${streamLabel(l.stream)}` : ''}`,
        tier: 1,
        colour: z?.zone_kind === 'STORE' ? '#7c3aed' : '#059669',
        metric: `${fmtN(z?.round_trip_km || l.round_trip_km)} km round trip`,
      });
    });
    return out;
  }, [setup.lanes, setup.carriers, setup.rates, setup.zones, setup.picks.sites]);

  const netEdges = useMemo(() => setup.lanes.map((l) => {
    const lc = setup.carriers.filter((c) => c.lane_id === l.lane_id);
    const cost = lc.reduce((a, c) => a + setup.rates.filter((r) => r.carrier_id === c.carrier_id)
      .reduce((b, r) => b + sum12(r.cost), 0), 0);
    const sh = shareByLane.get(l.lane_id) || { total: 0, count: 0 };
    const tone = sh.count === 0 ? 'empty' : Math.round(sh.total) !== 100 ? 'bad' : 'priced';
    return {
      id: l.lane_id,
      from: `S:${l.origin_site_code}`,
      to: `Z:${l.zone_code}:${l.stream || ''}`,
      label: sh.count === 0 ? 'no carrier' : fmtR(cost),
      tone,
      lane: l,
    };
  }), [setup.lanes, setup.carriers, setup.rates, shareByLane]);

  const resolvePending = async (action) => {
    const target = pendingLane;
    setPendingLane(null);
    if (action === 'cancel' || !target) return;
    if (action === 'save') { const ok = await saveLaneChanges(); if (!ok) return; }
    else setLaneDirty({ lanes: {}, carriers: {}, rates: {} });
    setSelLane(target);
  };

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Secondary Lanes &amp; Rates</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Who carries what share of each lane, and at what rate. The zones themselves, the customers mapped into them and the store delivery calendar live on Delivery Zones &amp; Customers.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            {/* Same page, two ways in. The rate card is the default because it
                is where the work is; the map answers "which zone have we not
                priced" faster than the list can. */}
            <div className="flex items-center rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
              {[['rates', 'Lanes & Rates'], ['network', 'Network']].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${view === v
                    ? 'bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                  {v === 'network' && <Waypoints className="w-3.5 h-3.5" />}{label}
                  {v === 'network' && badLanes > 0 && (
                    <span className="ml-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300">
                      {badLanes}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`flex-1 min-h-0 px-6 pb-6 space-y-3 ${view === 'network' ? 'flex flex-col' : 'overflow-y-auto'}`}>
        {error && <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        {toast && <div className="rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">{toast}</div>}

        {view === 'network' && (
          <LaneNetworkMap
            nodes={netNodes}
            edges={netEdges}
            selectedEdgeId={selLane}
            onSelectEdge={(e) => { if (e) requestLane(e.id); else setSelLane(null); }}
            loading={loading}
            emptyTitle="No lanes to draw yet."
            emptyHint="A lane is a depot serving a delivery zone. Add zones on Delivery Zones & Customers, then price them here."
            legend={<SecondaryNetworkLegend />}
            inspector={lane ? (
              <SecondaryLaneInspector
                lane={lane}
                carriers={laneCarriers}
                rates={setup.rates}
                picks={setup.picks}
                carrierVal={carrierVal}
                busy={busy}
                share={laneShare}
                cost={laneCost}
                onPatchCarrier={patchCarrier}
                onAddCarrier={() => run(() => secondaryService.addCarrier({
                  lane_id: lane.lane_id,
                  carrier_type: setup.picks.contractors.length ? 'CONTRACTED' : 'OWNED',
                  contractor_code: setup.picks.contractors[0]?.contractor_code,
                  vehicle_type_code: setup.picks.vehicleTypes[0]?.vehicle_type_code,
                }), 'Carrier added.', 'setup')}
                onRemoveCarrier={(id) => run(() => secondaryService.deleteCarrier(id), 'Carrier removed.', 'setup')}
                onSave={saveLaneChanges}
                onOpenFull={() => setView('rates')}
                dirty={laneHasChanges}
              />
            ) : null}
          />
        )}

        {view === 'rates' && (loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[310px_minmax(0,1fr)] gap-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Lanes</p>
                <button className={btn} disabled={busy || setup.zones.length === 0}
                  onClick={() => { setAdding(true); setDraft({ drops_per_run: 5 }); }}>
                  <Plus className="w-3.5 h-3.5" /> Add
                </button>
              </div>

              {setup.zones.length === 0 && (
                <p className="m-0 mb-2 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2.5 text-[11px] text-amber-700 dark:text-amber-300">
                  No delivery zones yet — a lane is priced against one. Add them on <strong>Delivery Zones &amp; Customers</strong> first.
                </p>
              )}

              {/* Every field labelled. The drops-per-run box is pre-filled,
                  so its placeholder never showed and the most consequential
                  number on the lane went in unnamed. */}
              {adding && (
                <div className="mb-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 space-y-2">
                  <label className="block">
                    <span className="block mb-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Zone</span>
                    <select className={`${field} w-full`} value={draft.zone_code || ''}
                      onChange={(e) => {
                        const z = setup.zones.find((x) => x.zone_code === e.target.value);
                        setDraft({ ...draft, zone_code: e.target.value, stream: z?.zone_kind === 'STORE' ? 'AMBIENT' : null });
                      }}>
                      <option value="">Select a zone…</option>
                      {setup.zones.map((z) => (
                        <option key={z.zone_code} value={z.zone_code}>
                          {z.zone_name} — {siteLabel(z.origin_site_code, z.origin_site_name)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {setup.zones.find((z) => z.zone_code === draft.zone_code)?.zone_kind === 'STORE' && (
                    <label className="block">
                      <span className="block mb-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Temperature stream</span>
                      <select className={`${field} w-full`} value={draft.stream || 'AMBIENT'} onChange={(e) => setDraft({ ...draft, stream: e.target.value })}>
                        {STREAMS.map((s) => <option key={s} value={s}>{streamLabel(s)}</option>)}
                      </select>
                    </label>
                  )}
                  <label className="block">
                    <span className="block mb-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Drops per run</span>
                    <input type="number" min="0.1" step="0.1" className={`${numField} w-full`}
                      value={draft.drops_per_run ?? ''} onChange={(e) => setDraft({ ...draft, drops_per_run: e.target.value })} />
                    <span className="block mt-1 text-[10px] text-slate-400">
                      Stops a vehicle covers in one run — what turns drops into trucks. Editable afterwards.
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <button className={btn} disabled={busy} onClick={async () => {
                      if (await run(() => secondaryService.addLane(draft), 'Lane added.')) setAdding(false);
                    }}>Add</button>
                    <button className={btn} onClick={() => setAdding(false)}>Cancel</button>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                {setup.lanes.length === 0 && <p className="m-0 px-3 py-6 text-center text-xs text-slate-400">No lanes yet.</p>}
                {setup.lanes.map((l) => {
                  const on = l.lane_id === lane?.lane_id;
                  const sh = shareByLane.get(l.lane_id) || { total: 0, count: 0 };
                  const bad = sh.count > 0 && Math.round(sh.total) !== 100;
                  const none = sh.count === 0;
                  return (
                    <button key={l.lane_id} onClick={() => requestLane(l.lane_id)}
                      className={`w-full text-left px-3 py-2 border-b border-slate-100 dark:border-slate-700/60 last:border-0 transition-colors ${on
                        ? 'bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900'
                        : bad ? 'bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/15'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/40'}`}>
                      <span className="flex items-center gap-1.5 text-xs font-semibold">
                        {(bad || none) && <TriangleAlert className={`w-3 h-3 shrink-0 ${on ? '' : 'text-red-600 dark:text-red-400'}`} />}
                        {l.origin_site_code} → {l.zone_name}
                      </span>
                      {/* The origin depot spelled out. The code alone is the
                          key, not the answer to "which depot is that". */}
                      <span className={`block text-[10px] ${on ? 'opacity-70' : 'text-slate-400'}`}>
                        from {l.origin_site_name || l.origin_site_code}
                      </span>
                      <span className={`block text-[10px] ${on ? 'opacity-70' : bad ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-slate-400'}`}>
                        {l.stream ? `${streamLabel(l.stream)} · ` : ''}
                        {none ? 'no carriers' : `${sh.count} carrier${sh.count === 1 ? '' : 's'} · ${fmtN(sh.total)}%`}
                      </span>
                    </button>
                  );
                })}
              </div>
              {badLanes > 0 && (
                <p className="m-0 mt-1.5 flex items-start gap-1.5 text-[11px] text-red-600 dark:text-red-400">
                  <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-px" />
                  {badLanes} lane{badLanes === 1 ? '' : 's'} do not allocate to 100%. The budget is out by exactly that gap — under-costed if short, over-costed if long.
                </p>
              )}
              <p className="m-0 mt-1.5 text-[11px] text-slate-400">
                A lane is <strong>origin site → zone → stream</strong>. The same zone served from two depots is two lanes at two costs — and the origin decides where it posts.
              </p>
            </div>

            <div className="space-y-3">
              {lane && (
                <>
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
                    <div className="flex flex-wrap items-end gap-5">
                      <div>
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Lane</span>
                        <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
                          {siteLabel(lane.origin_site_code, lane.origin_site_name)} → {lane.zone_name}{lane.stream ? ` · ${streamLabel(lane.stream)}` : ''}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Drops per run</span>
                        <input type="number" min="0.1" step="0.1" className={numField} style={{ width: 70 }}
                          value={laneVal(lane).drops_per_run ?? ''}
                          onChange={(e) => patchLane(lane.lane_id, { drops_per_run: Number(e.target.value) || 1 })} />
                      </div>
                      {[
                        ['Round-trip km', fmtN(lane.round_trip_km)],
                        ['Volume / yr', `${fmtN(sum12(lane.drivers?.volume))} ${(lane.load_uom || '').toLowerCase()}s`],
                        ['Drops / yr', fmtN(sum12(lane.drivers?.drops))],
                        ['Avg drop', fmtN(sum12(lane.drivers?.volume) / (sum12(lane.drivers?.drops) || 1), 1)],
                      ].map(([l2, v]) => (
                        <div key={l2}>
                          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{l2}</span>
                          <span className="font-mono text-sm text-slate-600 dark:text-slate-300">{v}</span>
                        </div>
                      ))}
                      <div className="ml-auto flex items-center gap-2">
                        <button className={btn} disabled={!laneHasChanges || busy}
                          onClick={() => setLaneDirty({ lanes: {}, carriers: {}, rates: {} })}><Undo2 className="w-3.5 h-3.5" /> Discard</button>
                        <button className={btnPrimary} disabled={!laneHasChanges || busy} onClick={saveLaneChanges}>
                          <Save className="w-3.5 h-3.5" /> Save
                        </button>
                        <button className={btn} disabled={busy}
                          onClick={() => run(() => secondaryService.deleteLane(lane.lane_id), 'Lane deleted.')}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="m-0 mt-2 text-[11px] text-slate-400">
                      <strong>Drops per run</strong> turns stops into trucks and is the most contested number in secondary distribution — so it sits here in the open rather than inside a formula.
                      Drops themselves come off the customer mapping: volume ÷ average drop size.
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Carriers on this lane</p>
                    <button className={btn} disabled={busy}
                      onClick={() => run(() => secondaryService.addCarrier({
                        lane_id: lane.lane_id,
                        carrier_type: setup.picks.contractors.length ? 'CONTRACTED' : 'OWNED',
                        contractor_code: setup.picks.contractors[0]?.contractor_code,
                        vehicle_type_code: setup.picks.vehicleTypes[0]?.vehicle_type_code,
                      }), 'Carrier added.')}>
                      <Plus className="w-3.5 h-3.5" /> Add carrier
                    </button>
                  </div>

                  {laneCarriers.length === 0 && (
                    <p className="m-0 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-6 text-center text-xs text-red-700 dark:text-red-300">
                      No carriers on this lane — it has volume but nothing to move it, so it costs nothing and it should not.
                    </p>
                  )}

                  {laneCarriers.map((c0) => {
                    const c = carrierVal(c0);
                    const rates = setup.rates.filter((r) => r.carrier_id === c0.carrier_id);
                    const total = rates.reduce((a, r) => a + sum12(r.cost), 0);
                    const cd = c0.drivers || {};
                    return (
                      <div key={c0.carrier_id} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                        <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-slate-50 dark:bg-slate-700/40 border-b border-slate-200 dark:border-slate-700">
                          <select className={field} value={c.carrier_type}
                            onChange={(e) => patchCarrier(c0.carrier_id, {
                              carrier_type: e.target.value,
                              contractor_code: e.target.value === 'OWNED' ? null : (c.contractor_code || setup.picks.contractors[0]?.contractor_code),
                            })}>
                            <option value="OWNED">Own fleet</option>
                            <option value="CONTRACTED">Contracted</option>
                          </select>
                          <div className="min-w-[190px]">
                            {c.carrier_type === 'OWNED'
                              ? <Pill tone={TONE.owned}>Own fleet</Pill>
                              : <SearchSelect value={c.contractor_code || ''}
                                  onChange={(v) => patchCarrier(c0.carrier_id, { carrier_type: 'CONTRACTED', contractor_code: v })}
                                  options={setup.picks.contractors.map((x) => ({ code: x.contractor_code, name: x.contractor_name }))}
                                  placeholder="Contractor…" />}
                          </div>
                          <select className={field} value={c.vehicle_type_code || ''}
                            onChange={(e) => patchCarrier(c0.carrier_id, { vehicle_type_code: e.target.value })}>
                            <option value="">— vehicle —</option>
                            {setup.picks.vehicleTypes.map((v) => <option key={v.vehicle_type_code} value={v.vehicle_type_code}>{v.description}</option>)}
                          </select>
                          <div className="flex items-center gap-1">
                            <input type="number" min="0" max="100" className={numField} style={{ width: 62 }}
                              value={c.allocation_pct ?? ''}
                              onChange={(e) => patchCarrier(c0.carrier_id, { allocation_pct: Number(e.target.value) || 0 })} />
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">% of lane</span>
                          </div>
                          <div className="ml-auto flex items-center gap-4 text-[11px] text-slate-500 dark:text-slate-400">
                            <span>drops <span className="font-mono text-slate-700 dark:text-slate-200">{fmtN(sum12(cd.drops))}</span></span>
                            <span>runs <span className="font-mono text-slate-700 dark:text-slate-200">{fmtN(sum12(cd.runs))}</span></span>
                            <span className="font-mono text-sm font-bold text-slate-800 dark:text-slate-100">{fmtR(total)}</span>
                            <button disabled={busy} onClick={() => run(() => secondaryService.deleteCarrier(c0.carrier_id), 'Carrier removed.')}
                              className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>

                        <table className="w-full border-collapse text-xs">
                          <thead><tr className="text-left">
                            {['Rate Component', 'Rate', 'Indexed To', 'Escl.', 'Allocates On', 'Posts To', 'Driver / yr', 'Cost / yr', ''].map((h) => <th key={h} className={th}>{h}</th>)}
                          </tr></thead>
                          <tbody>
                            {rates.map((r0) => {
                              const r = rateVal(r0);
                              const basis = RATE_BASES.find((b) => b.code === r.rate_basis) || RATE_BASES[0];
                              return (
                                <tr key={r0.rate_line_id} className="border-t border-slate-100 dark:border-slate-700/60">
                                  <td className="px-2 py-1.5">
                                    <select className={field} title={basis.hint} value={r.rate_basis}
                                      onChange={(e) => patchRate(r0.rate_line_id, { rate_basis: e.target.value })}>
                                      {RATE_BASES.map((b) => <option key={b.code} value={b.code}>{b.label}</option>)}
                                    </select>
                                    {r0.label && <span className="ml-1.5 text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">{r0.label}</span>}
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input type="number" step="0.01" className={numField} style={{ width: 92 }}
                                      value={r.rate ?? r0.rates?.[0] ?? 0}
                                      onChange={(e) => patchRate(r0.rate_line_id, { rate: Number(e.target.value) || 0 })} />
                                    {/* What the rate actually costs once the index
                                        is applied. Shown NEXT TO the typed number
                                        rather than replacing it — the base is what
                                        was negotiated and stays visible. */}
                                    {(() => {
                                      const fi = factorOf(r);
                                      const fc = increaseFactorOf(r, c0);
                                      const f = fi * fc;
                                      if (Math.abs(f - 1) < 0.0005) return null;
                                      const eff = Number(r.rate ?? r0.rates?.[0] ?? 0) * f;
                                      const why = [
                                        Math.abs(fi - 1) >= 0.0005 ? `index x${fi.toFixed(4)}` : null,
                                        Math.abs(fc - 1) >= 0.0005 ? `increase x${fc.toFixed(4)} (year average of the step)` : null,
                                      ].filter(Boolean).join(' · ');
                                      return (
                                        <span className={`block mt-0.5 text-[10px] font-mono ${f > 1 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}
                                          title={`Base x ${f.toFixed(4)} — ${why}`}>
                                          → {eff.toFixed(2)}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  {/* Index, the base it was quoted at, and how much
                                      of THIS rate is that commodity. Three fields
                                      because the arithmetic needs three, and hiding
                                      one makes the other two unreadable. */}
                                  <td className="px-2 py-1.5">
                                    <div className="flex items-center gap-1">
                                      <select className={field} style={{ width: 92 }} value={r.index_code || ''}
                                        title="Link this rate to a volatile price. Base and share are stamped from policy, then overridable here."
                                        onChange={(e) => linkRate(r0, e.target.value || null)}>
                                        <option value="">— none —</option>
                                        {indices.map((i) => <option key={i.index_code} value={i.index_code}>{i.index_name}</option>)}
                                      </select>
                                      {r.index_code && (
                                        <>
                                          <input type="number" min="0" max="100" step="1" className={numField} style={{ width: 52 }}
                                            title="What proportion of THIS rate is the commodity. 0 = a fixed-price contract, immune."
                                            value={r.index_share_pct ?? 0}
                                            onChange={(e) => patchRate(r0.rate_line_id, { index_share_pct: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })} />
                                          <span className="text-[9px] font-bold text-slate-400">%</span>
                                          <input type="number" min="0" step="0.01" className={numField} style={{ width: 64 }}
                                            title="The price already inside this rate — what the carrier quoted at, NOT the current price."
                                            value={r.index_base_value ?? 0}
                                            onChange={(e) => patchRate(r0.rate_line_id, { index_base_value: Number(e.target.value) || 0 })} />
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  {/* The carrier's annual increase, set once on
                                      Budget Setup → Contractors. Read-only here
                                      — the % and the month belong to the
                                      carrier, not the component — but the
                                      component can OPT OUT, which is what a
                                      locked contract needs. */}
                                  <td className="px-2 py-1.5 whitespace-nowrap">
                                    {(() => {
                                      const inc = increaseOf(c0);
                                      if (c0.carrier_type === 'OWNED') {
                                        return <span className="text-[10px] text-slate-400" title="Own fleet has no contractor and no escalation clause">—</span>;
                                      }
                                      if (!inc) {
                                        return <span className="text-[10px] text-slate-400" title="No expected increase for this carrier — set one on Budget Setup → Contractors">none</span>;
                                      }
                                      const on = r.apply_contractor_increase !== false;
                                      return (
                                        <label className="inline-flex items-center gap-1.5 cursor-pointer"
                                          title={`${inc.pct}% from period ${inc.period}. Untick for a component whose price is locked for the year.`}>
                                          <input type="checkbox" checked={on}
                                            onChange={(e) => patchRate(r0.rate_line_id, { apply_contractor_increase: e.target.checked })} />
                                          <span className={`text-[10px] font-mono ${on ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400 line-through'}`}>
                                            +{inc.pct.toFixed(1)}%
                                            <span className="block text-[9px] text-slate-400">P{inc.period}</span>
                                          </span>
                                        </label>
                                      );
                                    })()}
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <select className={field} value={r.alloc_base}
                                      title="How this component pushes back to customers. A per-run charge is consumed by STOPS, not by volume."
                                      onChange={(e) => patchRate(r0.rate_line_id, { alloc_base: e.target.value })}>
                                      <option value="DROPS">Drops</option>
                                      <option value="VOLUME">Volume</option>
                                    </select>
                                  </td>
                                  <td className="px-2 py-1.5 whitespace-nowrap">
                                    {r0.posting?.account_code
                                      ? <>
                                          <span className="font-mono text-slate-600 dark:text-slate-300">{r0.posting.account_code}</span>
                                          <span className="block text-[10px] text-slate-400">{r0.posting.cost_center_code || 'no cost centre'}</span>
                                        </>
                                      : <span className="text-[10px] text-amber-600 dark:text-amber-400" title="No rule covers this lane — set one under Budget Setup → Secondary Posting">not set</span>}
                                  </td>
                                  <td className="px-2 py-1.5 text-right font-mono text-slate-500 dark:text-slate-400">
                                    {fmtN(sum12(cd[basis.driver]))} <span className="text-[10px] text-slate-400">{basis.driver}</span>
                                  </td>
                                  <td className={`px-2 py-1.5 text-right font-mono font-semibold whitespace-nowrap ${sum12(r0.cost) < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-200'}`}>
                                    {fmtR(sum12(r0.cost))}
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <button disabled={busy} onClick={() => run(() => secondaryService.deleteRate(r0.rate_line_id), 'Rate component removed.')}
                                      className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t border-slate-100 dark:border-slate-700/60">
                              <td colSpan={9} className="px-2 py-1.5">
                                <button disabled={busy} className="text-[11px] font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
                                  onClick={() => run(() => secondaryService.addRate({ carrier_id: c0.carrier_id, rate_basis: 'PER_DROP' }), 'Rate component added.')}>
                                  + Add rate component
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })}

                  <div className={`rounded-xl border px-3 py-2 flex flex-wrap items-center justify-between gap-3 ${
                    laneCarriers.length > 0 && Math.round(laneShare) !== 100
                      ? 'border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70'}`}>
                    <div className="flex items-center gap-4 text-[11px]">
                      <span className={laneCarriers.length > 0 && Math.round(laneShare) !== 100
                        ? 'text-red-700 dark:text-red-300 font-bold' : 'text-slate-500 dark:text-slate-400'}>
                        Carrier share {fmtN(laneShare)}%
                      </span>
                      {runsSplit !== runsWhole && (
                        <span className="text-slate-400" title="Rounding up per carrier per period. Two carriers cannot share three-quarters of a truck, so a split lane needs more whole runs than a single carrier would.">
                          runs {fmtN(runsSplit)} split vs {fmtN(runsWhole)} whole-lane
                        </span>
                      )}
                    </div>
                    <span className="font-mono text-sm font-bold text-slate-800 dark:text-slate-100">Lane cost / year {fmtR(laneCost)}</span>
                  </div>

                  <p className="m-0 text-[11px] text-slate-400">
                    Share is on the <strong>carrier</strong>, not the rate component: &ldquo;R450 a drop <em>plus</em> R50 a pallet&rdquo; is one carrier&rsquo;s price with two components, not two carriers competing for a share.
                    Own-fleet carriers hold the variable cost of movement only — lease, insurance and licensing belong on Warehousing → Fixed Costs.
                    A negative rate is a <span className="font-semibold text-emerald-600 dark:text-emerald-400">backhaul credit</span>. Adding and deleting commit straight away; typed values wait for Save.
                  </p>
                  <p className="m-0 text-[11px] text-slate-400">
                    <strong>Indexed to</strong> links a rate to a volatile price — diesel — so one entry on Budget Setup → Price Indices moves it.
                    The <strong>%</strong> is how much of <em>this</em> rate is that commodity: a per-km line is around 40, a per-drop charge around 20
                    because most of it is handling and waiting, a per-pallet charge around 10. <strong>0 means immune</strong>, which is what a genuinely
                    fixed-price contract is. The last box is the price the carrier <em>quoted at</em>, not today&rsquo;s — a line quoted after a fuel rise
                    must not be charged for it twice. The typed rate is never overwritten; the arrow under it is what it costs once the index is applied.
                  </p>
                  <p className="m-0 text-[11px] text-slate-400">
                    <strong>Escl.</strong> is the carrier&rsquo;s expected annual increase, set once on Budget Setup → Contractors and applied as a
                    <em> step</em> from the month shown — untick it for a component whose price is locked for the year. It stacks on the index
                    (<span className="font-mono">base × diesel × increase</span>), so if a carrier&rsquo;s annual rise is how they recover fuel rather
                    than a separate adjustment clause, give that component a fuel share of <strong>0</strong> or diesel is counted twice.
                  </p>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Unsaved-changes gate on lane switch. Three answers, because discarding
          and cancelling are different intentions and a confirm() cannot tell
          them apart. */}
      {pendingLane && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-xl">
            <h3 className="m-0 mb-1 text-sm font-bold text-slate-800 dark:text-slate-100">Unsaved changes on this lane</h3>
            <p className="m-0 mb-3 text-xs text-slate-500 dark:text-slate-400">
              Save them before switching, or leave them behind?
            </p>
            <div className="flex justify-end gap-2">
              <button className={btn} onClick={() => resolvePending('cancel')}>Stay here</button>
              <button className={btn} onClick={() => resolvePending('discard')}>Discard</button>
              <button className={btnPrimary} onClick={() => resolvePending('save')}><Save className="w-3.5 h-3.5" /> Save &amp; switch</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Network view: legend ---------------------------------------------------
const SecondaryNetworkLegend = () => (
  <div className="text-xs text-slate-500 dark:text-slate-400 space-y-3">
    <p className="m-0 font-bold uppercase tracking-wider text-slate-400">What you are looking at</p>
    <p className="m-0">
      A star out of each depot, one arm per delivery zone it serves. Unlike the inter-depot map, the far
      end is a <strong>geography</strong> and never dispatches anything.
    </p>
    <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-slate-700">
      <p className="m-0 font-bold uppercase tracking-wider text-slate-400">Nodes</p>
      <p className="m-0"><span className="inline-block w-3 h-3 rounded align-middle mr-1.5" style={{ background: '#0284c7' }} /> Depot</p>
      <p className="m-0"><span className="inline-block w-3 h-3 rounded align-middle mr-1.5" style={{ background: '#059669' }} /> Customer zone</p>
      <p className="m-0"><span className="inline-block w-3 h-3 rounded align-middle mr-1.5" style={{ background: '#7c3aed' }} /> Store cluster</p>
    </div>
    <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-slate-700">
      <p className="m-0 font-bold uppercase tracking-wider text-slate-400">Lanes</p>
      <p className="m-0"><span className="inline-block w-6 border-t-2 border-emerald-600 align-middle mr-1.5" /> Priced, carriers add to 100%</p>
      <p className="m-0"><span className="inline-block w-6 border-t-2 border-red-600 align-middle mr-1.5" /> Carriers do not add to 100%</p>
      <p className="m-0"><span className="inline-block w-6 border-t-2 border-dashed border-slate-300 align-middle mr-1.5" /> No carrier — costs nothing, and should not</p>
    </div>
    <p className="m-0 pt-2 border-t border-slate-100 dark:border-slate-700">
      The same zone served from two depots draws as two arms, because it is two lanes at two costs.
      The layout is not saved — this map is a way in to a lane, not a document.
    </p>
  </div>
);

// ---- Network view: the lane popup ------------------------------------------
// Who carries this zone and at what share. Rate COMPONENTS stay on the full
// rate card: a carrier's quote is routinely several of them, and a 288px panel
// is the wrong place to argue about whether the pallet charge is per drop.
const SecondaryLaneInspector = ({
  lane, carriers, rates, picks, carrierVal, busy, share, cost,
  onPatchCarrier, onAddCarrier, onRemoveCarrier, onSave, onOpenFull, dirty,
}) => {
  const shareBad = carriers.length > 0 && Math.round(share) !== 100;
  return (
    <div className="space-y-3">
      <div>
        <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">Lane</p>
        <p className="m-0 mt-1 text-sm font-bold text-slate-800 dark:text-slate-100">{lane.origin_site_code}</p>
        <p className="m-0 text-base leading-none text-slate-400">↓</p>
        <p className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">
          {lane.zone_name}{lane.stream ? ` · ${streamLabel(lane.stream)}` : ''}
        </p>
        <p className="m-0 mt-1 text-[11px] text-slate-400">
          {fmtN(lane.round_trip_km)} km round trip · {fmtN(sum12(lane.drivers?.drops))} drops/yr
          {' · '}{fmtN(laneValDropsPerRun(lane))} drops per run
        </p>
      </div>

      <div className={`px-2 py-1.5 rounded-md text-[11px] ${shareBad
        ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 font-semibold'
        : 'bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400'}`}>
        Carrier share {fmtN(share)}% · {fmtR(cost)} / yr
        {shareBad && <span className="block mt-0.5 font-normal">The budget is out by exactly that gap.</span>}
      </div>

      {carriers.length === 0 && (
        <p className="m-0 rounded-md bg-amber-50 dark:bg-amber-500/10 px-2 py-2 text-[11px] text-amber-700 dark:text-amber-300">
          Nothing carries this zone yet — it has volume but nothing to move it.
        </p>
      )}

      {carriers.map((c0) => {
        const c = carrierVal(c0);
        const lines = rates.filter((r) => r.carrier_id === c0.carrier_id);
        return (
          <div key={c0.carrier_id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <select className={field} style={{ flex: 1 }} value={c.carrier_type}
                onChange={(e) => onPatchCarrier(c0.carrier_id, {
                  carrier_type: e.target.value,
                  contractor_code: e.target.value === 'OWNED' ? null : (c.contractor_code || picks.contractors[0]?.contractor_code),
                })}>
                <option value="OWNED">Own fleet</option>
                <option value="CONTRACTED">Contracted</option>
              </select>
              <button onClick={() => onRemoveCarrier(c0.carrier_id)} disabled={busy}
                className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {c.carrier_type === 'CONTRACTED' && (
              <SearchSelect value={c.contractor_code || ''}
                onChange={(v) => onPatchCarrier(c0.carrier_id, { carrier_type: 'CONTRACTED', contractor_code: v })}
                options={picks.contractors.map((x) => ({ code: x.contractor_code, name: x.contractor_name }))}
                placeholder="Contractor…" />
            )}

            <select className={`${field} w-full`} value={c.vehicle_type_code || ''}
              onChange={(e) => onPatchCarrier(c0.carrier_id, { vehicle_type_code: e.target.value })}>
              <option value="">— vehicle —</option>
              {picks.vehicleTypes.map((v) => <option key={v.vehicle_type_code} value={v.vehicle_type_code}>{v.description}</option>)}
            </select>

            <div className="flex items-center gap-1">
              <input type="number" min="0" max="100" className={numField} style={{ width: 58 }}
                value={c.allocation_pct ?? ''}
                onChange={(e) => onPatchCarrier(c0.carrier_id, { allocation_pct: Number(e.target.value) || 0 })} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">% of lane</span>
              <span className="ml-auto font-mono text-[11px] text-slate-600 dark:text-slate-300">
                {lines.length} rate line{lines.length === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        );
      })}

      <button className={btn} onClick={onAddCarrier} disabled={busy}>
        <Plus className="w-3.5 h-3.5" /> Add carrier
      </button>

      <div className="pt-2 border-t border-slate-100 dark:border-slate-700 space-y-1.5">
        <button className={`${btnPrimary} w-full justify-center`} onClick={onSave} disabled={busy || !dirty}>
          <Save className="w-3.5 h-3.5" /> Save
        </button>
        <button className={`${btn} w-full justify-center`} onClick={onOpenFull}>
          Open in Lanes &amp; Rates
        </button>
        <p className="m-0 text-[10px] text-slate-400">
          Rate components, diesel linking, the contractor increase and posting live on the full rate card —
          one carrier&rsquo;s quote is often several components, and that argument needs the width.
        </p>
      </div>
    </div>
  );
};

// Drops per run lives on the lane and is the number this panel most often
// needs quoting; read defensively because a lane created seconds ago may not
// carry it yet.
const laneValDropsPerRun = (l) => Number(l?.drops_per_run) || 0;

export default SecondaryLanes;
